import React, { useState, useEffect } from "react";
import Menu from "../Menu";
import { useNavigate } from "react-router-dom";

/* ─── Theme function ─────────────────────────── */
const getTheme = () => ({
  bg: 'var(--bg)',
  surface: 'var(--surface)',
  surface2: 'var(--surface2)',
  border: 'var(--border)',
  text: 'var(--text)',
  textMuted: 'var(--text-muted)',
  accent: 'var(--info)',
  info: 'var(--info)',
  warning: 'var(--warning)',
  danger: 'var(--danger)',
  accent2: 'var(--info2)',
  accent3: 'var(--info3)',
});

/* ─── Inline styles (scoped to this page) ─────────────────────────── */
const S = {
  wrapper: {
    background: 'var(--bg)',
    minHeight: '100vh',
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    color: 'var(--text)',
  },
  page: {
    padding: '24px 28px',
    maxWidth: 1300,
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: 'var(--text)',
    letterSpacing: '-0.3px',
  },
  breadcrumb: {
    fontSize: 13,
    color: 'var(--text-muted)',
    display: 'flex',
    gap: 6,
    alignItems: 'center',
  },
  breadLink: { color: 'var(--info)', textDecoration: 'none' },
  // Period tabs
  tabRow: {
    display: 'flex',
    gap: 6,
    background: 'var(--surface)',
    padding: '5px 6px',
    borderRadius: 12,
    width: 'fit-content',
    marginBottom: 24,
    border: '1px solid var(--border)',
  },
  tab: (active) => ({
    padding: '8px 22px',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: active ? 700 : 500,
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.18s ease',
    background: active ? 'var(--info)' : 'transparent',
    color: active ? '#0d1117' : 'var(--text-muted)',
    boxShadow: active ? '0 2px 12px rgba(16,185,129,0.35)' : 'none',
  }),
  // Selectors row
  selectRow: {
    display: 'flex',
    gap: 16,
    marginBottom: 28,
    flexWrap: 'wrap',
  },
  selectGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    flex: '1 1 200px',
    maxWidth: 300,
  },
  selectLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  select: {
    padding: '10px 14px',
    borderRadius: 8,
    border: '1px solid var(--border)',
    background: 'var(--surface)',
    color: 'var(--text)',
    fontSize: 14,
    outline: 'none',
    cursor: 'pointer',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    paddingRight: 36,
  },
  // Cards
  card: {
    background: 'var(--surface)',
    borderRadius: 16,
    border: '1px solid var(--border)',
    marginBottom: 20,
    overflow: 'hidden',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '18px 22px 10px',
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: 'var(--text)',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  cardIcon: {
    width: 28,
    height: 28,
    background: 'rgba(16,185,129,0.15)',
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    color: 'var(--info)',
  },
  // Stats summary row
  statGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: 12,
    padding: '0 22px 18px',
  },
  statCard: {
    background: 'var(--surface2)',
    borderRadius: 10,
    padding: '14px 16px',
    border: '1px solid var(--border)',
  },
  statLabel: { fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 },
  statValue: { fontSize: 22, fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.5px' },
  // Table
  tableWrap: { overflowX: 'auto', padding: '0 22px 22px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    padding: '10px 14px',
    textAlign: 'left',
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    borderBottom: '1px solid var(--border)',
    whiteSpace: 'nowrap',
  },
  thCenter: {
    padding: '10px 14px',
    textAlign: 'center',
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    borderBottom: '1px solid var(--border)',
    whiteSpace: 'nowrap',
  },
  tr: (i) => ({
    background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)',
    transition: 'background 0.15s',
  }),
  td: { padding: '14px 14px', color: 'var(--text)', borderBottom: '1px solid rgba(30,39,64,0.6)', verticalAlign: 'middle' },
  tdCenter: { padding: '14px 14px', textAlign: 'center', color: 'var(--text-muted)', borderBottom: '1px solid rgba(30,39,64,0.6)', verticalAlign: 'middle' },
  // Index badge
  badge: (color) => ({
    width: 30,
    height: 30,
    borderRadius: '50%',
    background: color || 'rgba(99,102,241,0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 700,
    color: '#fff',
    margin: '0 auto',
  }),
  // Time formatting
  teal: { color: 'var(--info)', fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  purple: { color: 'var(--info2)', fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  // Progress bar
  progressWrap: { display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' },
  progressBar: (pct) => ({
    width: 80,
    height: 6,
    borderRadius: 3,
    background: 'var(--border)',
    position: 'relative',
    overflow: 'hidden',
  }),
  progressFill: (pct) => ({
    position: 'absolute',
    left: 0, top: 0, bottom: 0,
    width: `${Math.min(pct, 100)}%`,
    background: pct >= 80 ? 'var(--info)' : pct >= 60 ? 'var(--warning)' : 'var(--danger)',
    borderRadius: 3,
  }),
  pctText: (pct) => ({
    fontSize: 12,
    fontWeight: 700,
    color: pct >= 80 ? 'var(--info)' : pct >= 60 ? 'var(--warning)' : 'var(--danger)',
    minWidth: 32,
  }),
  // POS name pill
  namePill: (color) => ({
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: 20,
    background: color,
    fontWeight: 600,
    fontSize: 13,
    color: '#fff',
  }),
  // Sort dropdown
  sortSelect: {
    padding: '7px 28px 7px 12px',
    borderRadius: 8,
    border: '1px solid var(--border)',
    background: 'var(--surface)',
    color: 'var(--text-muted)',
    fontSize: 13,
    outline: 'none',
    cursor: 'pointer',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: 'var(--text-muted)',
  },
  spinner: {
    display: 'inline-block',
    width: 28,
    height: 28,
    border: '3px solid var(--border)',
    borderTop: '3px solid var(--info)',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    marginBottom: 12,
  },
};

const PILL_COLORS = ['#b45309', '#065f46', '#1e3a5f', '#7f1d1d', '#4c1d95', '#0e7490', '#14532d', '#831843'];

// ─── Component ──────────────────────────────────────────────────────
const POSStatistics = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('all');
  const [loading, setLoading] = useState(false);
  const [report10Data, setReport10Data] = useState(null);
  const [poss, setPoss] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [selectedPOS, setSelectedPOS] = useState('');
  const [selectedSchedule, setSelectedSchedule] = useState('');
  const [posLoading, setPosLoading] = useState(false);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [sortBy, setSortBy] = useState('played');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const API_BASE = 'https://api.dqr-display-tms.bonrix.in/api/SchedulerStatistics';
  const AUTH_TOKEN = getCookie("token");

  const getDateRange = (p) => {
    const now = new Date();
    if (p === 'all') return { startdate: 0, enddate: 0, isalltime: true };
    if (p === 'custom') {
      if (customStartDate && customEndDate) {
        const start = new Date(customStartDate);
        const end = new Date(customEndDate);
        end.setHours(23, 59, 59, 999);
        return { 
          startdate: Math.floor(start.getTime() / 1000), 
          enddate: Math.floor(end.getTime() / 1000), 
          isalltime: false 
        };
      }
      return { startdate: 0, enddate: 0, isalltime: false };
    }
    
    let startdate, enddate = Math.floor(now.getTime() / 1000);
    
    if (p === 'today') {
      startdate = Math.floor(new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() / 1000);
    } else if (p === 'thisweek') {
      const dayOfWeek = now.getDay();
      const sunday = new Date(now);
      sunday.setDate(now.getDate() - dayOfWeek);
      sunday.setHours(0, 0, 0, 0);
      startdate = Math.floor(sunday.getTime() / 1000);
    } else if (p === 'week') {
      startdate = Math.floor((now.getTime() - 7 * 86400000) / 1000);
    } else if (p === 'last7days') {
      startdate = Math.floor((now.getTime() - 7 * 86400000) / 1000);
    } else if (p === 'last30days') {
      startdate = Math.floor((now.getTime() - 30 * 86400000) / 1000);
    } else if (p === 'month') {
      startdate = Math.floor((now.getTime() - 30 * 86400000) / 1000);
    }
    
    return { startdate, enddate, isalltime: false };
  };

  const callAPI = async (endpoint, payload) => {
    const res = await fetch(`${API_BASE}/${endpoint}`, {
      method: 'POST',
      headers: { 'accept': 'text/plain', 'UserId': getCookie("userid"), 'X-Authorization': AUTH_TOKEN, 'Content-Type': 'application/json-patch+json' },
      body: JSON.stringify(payload)
    });
    const responseText = await res.text();
    
    // Check if response is empty or invalid
    if (!responseText || responseText.trim() === '') {
      console.warn('Empty response from API:', endpoint);
      return { isresponse: false };
    }
    
    // Try to parse as JSON
    try {
      return JSON.parse(responseText);
    } catch (parseError) {
      console.error('JSON parse error:', parseError, 'Response text:', responseText);
      return { isresponse: false };
    }
  };

  const callGetAPI = async (url) => {
    const res = await fetch(url, { headers: { 'accept': 'text/plain', 'UserId': getCookie("userid"), 'X-Authorization': AUTH_TOKEN } });
    const responseText = await res.text();
    
    // Check if response is empty or invalid
    if (!responseText || responseText.trim() === '') {
      console.warn('Empty response from API:', url);
      return { isresponse: false };
    }
    
    // Try to parse as JSON
    try {
      return JSON.parse(responseText);
    } catch (parseError) {
      console.error('JSON parse error:', parseError, 'Response text:', responseText);
      return { isresponse: false };
    }
  };

  const loadPOS = async () => {
    setPosLoading(true);
    try {
      const r = await callGetAPI('https://api.dqr-display-tms.bonrix.in/api/POSOperation/GetDistinct/posname');
      if (r.isresponse && r.data) setPoss(r.data);
    } finally { setPosLoading(false); }
  };

  const loadSchedules = async () => {
    setScheduleLoading(true);
    try {
      const r = await callGetAPI('https://api.dqr-display-tms.bonrix.in/api/POSSchedulerOperation/GetDistinct/name');
      if (r.isresponse && r.data) setSchedules(r.data);
    } finally { setScheduleLoading(false); }
  };

  const loadReport10 = async (posId) => {
    if (!posId) return;
    setLoading(true);
    try {
      const r = await callAPI('report10', { ...getDateRange(period), posid: parseInt(posId) });
      setReport10Data(r);
    } finally { setLoading(false); }
  };

  const loadReport11 = async (scheduleId) => {
    if (!scheduleId) return;
    setLoading(true);
    try {
      const r = await callAPI('report11', { ...getDateRange(period), posid: 0, scheduleid: parseInt(scheduleId) });
      setReport10Data(r);
    } finally { setLoading(false); }
  };

  const handlePOSChange = (val) => {
    setSelectedPOS(val);
    setSelectedSchedule('');
    setReport10Data(null);
    if (val) loadReport10(val);
  };

  const handleScheduleChange = (val) => {
    setSelectedSchedule(val);
    setSelectedPOS('');
    setReport10Data(null);
    if (val) loadReport11(val);
  };

  useEffect(() => {
    loadPOS();
    loadSchedules();
  }, []);

  useEffect(() => {
    if (selectedPOS) loadReport10(selectedPOS);
    else if (selectedSchedule) loadReport11(selectedSchedule);
  }, [period]);

  const secToHHMM = (sec) => {
    if (!sec) return '00:00';
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
  };

  const fmt = (n) => (n ?? 0).toLocaleString();

  const rows = report10Data?.isresponse && report10Data?.data ? report10Data.data : [];

  const sortedRows = [...rows].sort((a, b) => {
    if (sortBy === 'played') return (b.totalrunningtime || 0) - (a.totalrunningtime || 0);
    if (sortBy === 'cycles') return (b.avgcompletecyclecount || 0) - (a.avgcompletecyclecount || 0);
    if (sortBy === 'interrupts') return (b.avginterruptscount || 0) - (a.avginterruptscount || 0);
    return 0;
  });

  const totalRunning = rows.reduce((s, r) => s + (r.totalrunningtime || 0), 0);
  const avgCycles = rows.length ? rows.reduce((s, r) => s + (r.avgcompletecyclecount || 0), 0) / rows.length : 0;
  const avgInterrupts = rows.length ? rows.reduce((s, r) => s + (r.avginterruptscount || 0), 0) / rows.length : 0;
  const completionPct = rows.length ? Math.round((rows.filter(r => (r.avgcompletecyclecount || 0) > 0).length / rows.length) * 100) : 0;

  const periodLabel = { 
    today: 'Today', 
    thisweek: 'This Week', 
    last7days: 'Last 7 Days', 
    last30days: 'Last 30 Days', 
    month: 'This Month', 
    all: 'All Time', 
    custom: 'Custom' 
  }[period];

  return (
    <>
      <Menu />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }
        .cs-tr:hover td { background: rgba(16,185,129,0.04) !important; }
        .cs-select:focus { border-color: var(--info) !important; box-shadow: 0 0 0 3px rgba(16,185,129,0.12); }
        .cs-tab:hover { color: var(--text) !important; }
      `}</style>
      <div style={S.wrapper}>
        <div className="content-wrapper" style={{ background: 'transparent' }}>
          <div style={S.page}>

            {/* Header */}
            <div style={S.header}>
              <div style={S.title}>
                📊 POS Statistics
              </div>
              <div style={S.breadcrumb}>
                <a href="/dashboard" style={S.breadLink}>Home</a>
                <span>›</span>
                <a href="/pos-dashboard" style={S.breadLink}>POS Dashboard</a>
                <span>›</span>
                <span style={{ color: 'var(--text-muted)' }}>Statistics</span>
              </div>
            </div>

            {/* Period Tabs */}
            <div style={S.tabRow}>
              {['today','thisweek','last7days','last30days','month','all','custom'].map(p => (
                <button
                  key={p}
                  className="cs-tab"
                  style={S.tab(period === p)}
                  onClick={() => setPeriod(p)}
                >
                  {p === 'today' ? 'Today' : 
                   p === 'thisweek' ? 'This Week' : 
                   p === 'last7days' ? 'Last 7 Days' : 
                   p === 'last30days' ? 'Last 30 Days' : 
                   p === 'month' ? 'This Month' : 
                   p === 'all' ? 'All Time' : 
                   'Custom'}
                </button>
              ))}
            </div>

            {/* Selectors Row */}
            <div style={S.selectRow}>
              <div style={S.selectGroup}>
                <label style={S.selectLabel}>Select POS</label>
                <select
                  className="cs-select"
                  style={S.select}
                  value={selectedPOS}
                  onChange={e => handlePOSChange(e.target.value)}
                  disabled={posLoading}
                >
                  <option value="">All POS</option>
                  {poss.map(c => <option key={c.id} value={c.id}>{c.value}</option>)}
                </select>
              </div>
              <div style={S.selectGroup}>
                <label style={S.selectLabel}>Select Schedule</label>
                <select
                  className="cs-select"
                  style={S.select}
                  value={selectedSchedule}
                  onChange={e => handleScheduleChange(e.target.value)}
                  disabled={scheduleLoading}
                >
                  <option value="">All Schedules</option>
                  {schedules.map(s => <option key={s.id} value={s.id}>{s.value}</option>)}
                </select>
              </div>
            </div>

            {/* Custom Date Picker */}
            {period === 'custom' && (
              <div style={{ marginBottom: 24, padding: 16, background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Start Date</label>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: 6,
                        border: '1px solid var(--border)',
                        background: 'var(--surface2)',
                        color: 'var(--text)',
                        fontSize: 14,
                        outline: 'none',
                        cursor: 'pointer'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>End Date</label>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: 6,
                        border: '1px solid var(--border)',
                        background: 'var(--surface2)',
                        color: 'var(--text)',
                        fontSize: 14,
                        outline: 'none',
                        cursor: 'pointer'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <button
                      onClick={() => {
                        if (selectedPOS) loadReport10(selectedPOS);
                        else if (selectedSchedule) loadReport11(selectedSchedule);
                      }}
                      disabled={!customStartDate || !customEndDate}
                      style={{
                        padding: '8px 16px',
                        borderRadius: 6,
                        border: 'none',
                        background: customStartDate && customEndDate ? 'var(--info)' : '#374151',
                        color: '#fff',
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: customStartDate && customEndDate ? 'pointer' : 'not-allowed',
                        transition: 'all 0.2s'
                      }}
                    >
                      Apply Dates
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Loading */}
            {loading && (
              <div style={S.emptyState}>
                <div style={S.spinner} />
                <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading statistics...</div>
              </div>
            )}

            {/* Empty prompt */}
            {!loading && rows.length === 0 && (
              <div style={S.emptyState}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📂</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>No data yet</div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Select a POS or schedule above to view statistics</div>
              </div>
            )}

            {/* Main content */}
            {!loading && rows.length > 0 && (
              <>
                {/* Summary Stats */}
                <div style={S.statGrid}>
                  <div style={S.statCard}>
                    <div style={S.statLabel}>Total Catalogues</div>
                    <div style={S.statValue}>{fmt(rows.length)}</div>
                  </div>
                  <div style={S.statCard}>
                    <div style={S.statLabel}>Total Running Time</div>
                    <div style={{ ...S.statValue, color: 'var(--info)' }}>{secToHHMM(totalRunning)}</div>
                  </div>
                  <div style={S.statCard}>
                    <div style={S.statLabel}>Avg Complete Cycles</div>
                    <div style={{ ...S.statValue, color: 'var(--info2)' }}>{avgCycles.toFixed(1)}</div>
                  </div>
                  <div style={S.statCard}>
                    <div style={S.statLabel}>Avg Interrupts</div>
                    <div style={{ ...S.statValue, color: 'var(--warning)' }}>{avgInterrupts.toFixed(1)}</div>
                  </div>
                </div>

                {/* Detailed Table Card */}
                <div style={S.card}>
                  <div style={S.cardHeader}>
                    <div style={S.cardTitle}>
                      <span style={{ ...S.cardIcon, background: 'rgba(129,140,248,0.15)', color: 'var(--info2)' }}>≡</span>
                      Detailed POS Statistics
                    </div>
                    <select
                      style={S.sortSelect}
                      value={sortBy}
                      onChange={e => setSortBy(e.target.value)}
                    >
                      <option value="played">Sort: Played Time</option>
                      <option value="cycles">Sort: Avg Cycles</option>
                      <option value="interrupts">Sort: Avg Interrupts</option>
                    </select>
                  </div>
                  <div style={S.tableWrap}>
                    <table style={S.table}>
                      <thead>
                        <tr>
                          <th style={S.thCenter}>#</th>
                          <th style={S.th}>Catalogue Name</th>
                          <th style={S.th}>POS Name</th>
                          <th style={S.thCenter}>Running Time</th>
                          <th style={S.thCenter}>Avg Complete Cycles</th>
                          <th style={S.thCenter}>Avg Interrupts</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedRows.map((pos, i) => {
                          const color = PILL_COLORS[i % PILL_COLORS.length];
                          return (
                            <tr key={`${pos.catalogid}-${pos.posid}-${i}`} className="cs-tr" style={S.tr(i)}>
                              <td style={S.tdCenter}>
                                <div style={S.badge(color)}>{i + 1}</div>
                              </td>
                              <td style={S.td}>
                                <span style={S.namePill(color)}>
                                  {pos.catalogname || '—'}
                                </span>
                              </td>
                              <td style={S.td}>
                                <span style={{ color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: 12 }}>
                                  {pos.posname || '—'}
                                </span>
                              </td>
                              <td style={S.tdCenter}>
                                <span style={S.teal}>{secToHHMM(pos.totalrunningtime)}</span>
                              </td>
                              <td style={S.tdCenter}>
                                <span style={{ color: 'var(--info2)', fontWeight: 600 }}>
                                  {(pos.avgcompletecyclecount || 0).toFixed(2)}
                                </span>
                              </td>
                              <td style={S.tdCenter}>
                                <span style={{ color: 'var(--warning)', fontWeight: 600 }}>
                                  {(pos.avginterruptscount || 0).toFixed(2)}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Footer */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 22px', borderTop: '1px solid var(--border)' }}>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>SignalLens Analytics · Digital Signage POS Platform</span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      Data period: <span style={{ color: 'var(--info)', fontWeight: 700 }}>{periodLabel}</span>
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default POSStatistics;
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}
